const soundPath = "/audio/vote.ogg";
let muted = false;
let realtimeAudioContext = null;
let lastRealtimeSoundAt = 0;
let lastLocalVoteAt = 0;
let lastLocalVoteMarketId = "";
let pendingLocalVoteAt = 0;
let pendingLocalVoteMarketId = "";
let audioPlaybackUnlocked = false;
let audioUnlockListenersRegistered = false;
const realtimeSuppressMs = 2000;
const audioUnlockEvents = ["pointerdown", "mousedown", "keydown", "touchend"];

export function initAudioPreferences() {
  const browserWindow = getBrowserWindow();
  muted = browserWindow?.localStorage?.getItem("pm-muted") === "1";
  registerAudioUnlockListeners(browserWindow);
  return muted;
}

export function toggleMute() {
  markAudioPlaybackUnlocked();
  muted = !muted;
  getBrowserWindow()?.localStorage?.setItem("pm-muted", muted ? "1" : "0");
  return muted;
}

export function isMuted() {
  return muted;
}

export async function playVoteSound() {
  if (muted) return;
  try {
    const audio = new Audio(soundPath);
    audio.volume = 0.4;
    await audio.play();
  } catch {
    // Ignore playback failures due to browser autoplay policy.
  }
}

export function markLocalVoteSound(marketId) {
  lastLocalVoteAt = Date.now();
  lastLocalVoteMarketId = String(marketId || "");
  pendingLocalVoteAt = 0;
  pendingLocalVoteMarketId = "";
}

export function beginLocalVoteSuppression(marketId) {
  pendingLocalVoteAt = Date.now();
  pendingLocalVoteMarketId = String(marketId || "");
}

export function cancelLocalVoteSuppression(marketId) {
  const targetMarket = String(marketId || "");
  if (pendingLocalVoteMarketId.length === 0) return;
  if (pendingLocalVoteMarketId !== targetMarket) return;
  pendingLocalVoteAt = 0;
  pendingLocalVoteMarketId = "";
}

export function shouldSuppressRealtimeSound(marketId) {
  const now = Date.now();
  const targetMarket = String(marketId || "");

  if (
    pendingLocalVoteMarketId.length > 0 &&
    pendingLocalVoteMarketId === targetMarket
  ) {
    if (now - pendingLocalVoteAt <= realtimeSuppressMs) {
      return true;
    }
    pendingLocalVoteAt = 0;
    pendingLocalVoteMarketId = "";
  }

  if (now - lastLocalVoteAt > realtimeSuppressMs) {
    return false;
  }

  return lastLocalVoteMarketId.length > 0 && lastLocalVoteMarketId === targetMarket;
}

export function playRealtimeUpdateSound() {
  if (muted || !canUseAudioPlayback()) return;

  const now = Date.now();
  if (now - lastRealtimeSoundAt < 150) {
    return;
  }
  lastRealtimeSoundAt = now;

  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    if (!realtimeAudioContext || realtimeAudioContext.state === "closed") {
      realtimeAudioContext = new AudioCtx();
    }

    if (realtimeAudioContext.state === "suspended") {
      realtimeAudioContext.resume().catch(() => null);
    }

    const startTime = realtimeAudioContext.currentTime;
    const noteDuration = 0.075;
    const gap = 0.03;
    const frequencies = [880, 1175];

    for (let index = 0; index < frequencies.length; index += 1) {
      const noteStart = startTime + index * (noteDuration + gap);
      const noteEnd = noteStart + noteDuration;

      const oscillator = realtimeAudioContext.createOscillator();
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(frequencies[index], noteStart);

      const gain = realtimeAudioContext.createGain();
      gain.gain.setValueAtTime(0.0001, noteStart);
      gain.gain.linearRampToValueAtTime(0.1, noteStart + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, noteEnd);

      oscillator.connect(gain);
      gain.connect(realtimeAudioContext.destination);

      oscillator.start(noteStart);
      oscillator.stop(noteEnd + 0.005);
    }
  } catch {
    // Ignore playback failures due to browser autoplay policy.
  }
}

function canUseAudioPlayback() {
  if (audioPlaybackUnlocked) return true;
  const userActivation = getBrowserWindow()?.navigator?.userActivation;
  return userActivation?.hasBeenActive === true;
}

function registerAudioUnlockListeners(browserWindow) {
  if (!browserWindow || audioUnlockListenersRegistered) return;
  if (typeof browserWindow.addEventListener !== "function") return;
  audioUnlockListenersRegistered = true;
  for (const eventName of audioUnlockEvents) {
    browserWindow.addEventListener(eventName, markAudioPlaybackUnlocked, {
      once: true,
      passive: true,
      capture: true
    });
  }
}

function markAudioPlaybackUnlocked() {
  audioPlaybackUnlocked = true;
}

function getBrowserWindow() {
  return typeof window === "undefined" ? null : window;
}
